import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import {
  ClipCategory, ClipLabel, ClipLabelEdit, ClipLabelDimension, ClipLabelVocabulary,
} from 'services/VideoEditingService';
import { formatDuration, humanize } from '../format';
import ClipLabelFields, { Draft, clipLabelEditFrom, draftFrom } from './ClipLabelFields';
import ClipLabelSummary from './ClipLabelSummary';

interface ClipLabelEditorProps {
  // Every label for the selected clip: one whole-clip label plus one per sub-range.
  labels: ClipLabel[];
  vocabulary: ClipLabelVocabulary | null;
  // The human-curated category vocabulary. Omitted (or null) hides the category field
  // entirely, so a surface that hasn't loaded it doesn't render an empty picker.
  categories?: ClipCategory[] | null;
  // Create a category that doesn't exist yet and return it, so the picker can select it.
  onCreateCategory?: (name: string) => Promise<ClipCategory>;
  loading?: boolean;
  onSave: (labelId: string, edit: ClipLabelEdit) => Promise<void>;
}

// The draft shape, `draftFrom` and the request-body assembly live in ClipLabelFields, shared
// with the B-Roll Library's Edit modal so the two surfaces can't drift on the product→SKU
// invalidation or the "don't send categories we never showed" rule.

function OneLabel({
  label, vocabulary, categories, onCreateCategory, onSave,
}: {
  label: ClipLabel;
  vocabulary: ClipLabelVocabulary | null;
  categories?: ClipCategory[] | null;
  onCreateCategory?: ClipLabelEditorProps['onCreateCategory'];
  onSave: ClipLabelEditorProps['onSave'];
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftFrom(label));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A re-label (or another editor's save) replaces the row underneath us; drop any
  // stale draft rather than showing values that no longer exist.
  useEffect(() => {
    if (!editing) setDraft(draftFrom(label));
  }, [label, editing]);

  const dimensions = useMemo(() => vocabulary?.dimensions || [], [vocabulary]);
  const products = useMemo(() => vocabulary?.products || [], [vocabulary]);
  const productName = useMemo(
    () => products.find(p => p.handle === label.productHandle)?.name || null,
    [products, label.productHandle],
  );
  const dimensionByKey = useMemo(() => {
    const map = new Map<string, ClipLabelDimension>();
    for (const d of dimensions) map.set(d.key, d);
    return map;
  }, [dimensions]);

  const range = label.scope === 'segment' && label.inSec != null && label.outSec != null
    ? `${formatDuration(label.inSec)} – ${formatDuration(label.outSec)}`
    : null;

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      // clipLabelEditFrom carries the two non-obvious rules (never send clipType; send
      // categories only when the field was offered) — see ClipLabelFields.
      await onSave(label.labelId, clipLabelEditFrom(draft, { categoriesOffered: !!categories }));
      setEditing(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  }

  const valuesFor = (key: string) => (dimensionByKey.get(key)?.values || []).map(v => v.value);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mb: 1,
        borderLeft: label.scope === 'segment' ? '3px solid #C7D2FE' : '3px solid #6366F1',
        backgroundColor: label.scope === 'segment' ? '#FAFAFF' : '#FFFFFF',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#475569' }}>
          {label.scope === 'segment' ? `Sub-clip ${range || ''}` : 'Whole clip'}
        </Typography>
        <Tooltip title={label.labelSource === 'human'
          ? `Edited by ${label.editedBy || 'someone'} — a re-label run won't overwrite this`
          : `Labeled by ${label.model || 'the model'}`}>
          <Chip
            size="small"
            icon={label.labelSource === 'human' ? <PersonOutlineIcon sx={{ fontSize: 13 }} /> : <AutoAwesomeIcon sx={{ fontSize: 13 }} />}
            label={label.labelSource === 'human' ? 'edited' : 'AI'}
            sx={{ height: 20, fontSize: 11 }}
            variant="outlined"
          />
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        {!editing && (
          <Tooltip title="Edit these labels">
            <IconButton size="small" onClick={() => { setDraft(draftFrom(label)); setEditing(true); }}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {!editing ? (
        <ClipLabelSummary
          draft={draft}
          vocabulary={vocabulary}
          categories={categories}
          humanLabeled={label.labelSource === 'human'}
        />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <ClipLabelFields
            draft={draft}
            onDraftChange={setDraft}
            vocabulary={vocabulary}
            categories={categories}
            onCreateCategory={onCreateCategory}
            showConfidence={label.labelSource !== 'human'}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={() => { setEditing(false); setError(null); setDraft(draftFrom(label)); }} disabled={saving}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} /> : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

/**
 * The AI labels for one clip, with inline editing. Shows the whole-clip label first,
 * then each sub-clip range the labeler split out. An edited label is marked so a
 * later re-label run leaves it alone.
 */
export default function ClipLabelEditor({
  labels, vocabulary, categories, onCreateCategory, loading, onSave,
}: ClipLabelEditorProps) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Loading labels…</Typography>
      </Box>
    );
  }
  if (!labels.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        This clip hasn’t been labeled yet. Labels are generated per folder by the clip-labeling job.
      </Typography>
    );
  }

  // Whole-clip label first, then sub-clips in timeline order.
  const ordered = [...labels].sort((a, b) => {
    if (a.scope !== b.scope) return a.scope === 'clip' ? -1 : 1;
    return (a.inSec ?? 0) - (b.inSec ?? 0);
  });

  return (
    <Box>
      {ordered.map(label => (
        <OneLabel
          key={label.labelId}
          label={label}
          vocabulary={vocabulary}
          categories={categories}
          onCreateCategory={onCreateCategory}
          onSave={onSave}
        />
      ))}
    </Box>
  );
}
